import type { DataQualityIssueDto } from "@bi/types";
import { fmtBRL } from "./format";
import type { AttentionPoint } from "./intelligence/executive";

/**
 * Leituras da tela Qualidade dos Dados: o que cada verificação significa para
 * quem usa o BI, onde ela pesa e o que fazer no ERP. Os códigos (issueType)
 * vêm de apps/api/src/modules/data-quality/data-quality.service.ts.
 */
export type Severity = DataQualityIssueDto["severity"];

export type Check = {
  type: string;
  title: string;
  severity: Severity;
  /** onde o problema distorce o BI */
  why: string;
  /** o que fazer na origem */
  action: string;
  href: string;
  hrefLabel: string;
};

export const CHECKS: Check[] = [
  { type: "long_overdue_high_value", severity: "critical", title: "Título a receber vencido há mais de 90 dias (acima de R$ 5 mil)",
    why: "Ou a cobrança parou, ou o título já foi pago e falta a baixa no ERP. Nos dois casos o contas a receber e o fluxo de caixa ficam inflados.",
    action: "Confirme com o financeiro: se foi pago, dê baixa no ERP; se não foi, priorize a cobrança.", href: "/financeiro", hrefLabel: "Abrir Financeiro" },
  { type: "implausible_fuel_odometer", severity: "warning", title: "Abastecimento com odômetro impossível",
    why: "O km desde o abastecimento anterior passa do teto de 3.000 km. O BI descarta esse trecho do km e do km/l da frota, então a média fica com menos dados.",
    action: "Corrija o odômetro do lançamento no ERP (costuma ser erro de digitação ou troca de painel).", href: "/frota/combustivel", hrefLabel: "Abrir Combustível" },
  { type: "zero_total_cte", severity: "warning", title: "CT-e autorizado com valor zero ou negativo",
    why: "Conta como viagem sem receita: derruba o frete médio e a margem.",
    action: "Confira se o CT-e deveria estar cancelado ou se o valor foi lançado errado.", href: "/operacional", hrefLabel: "Abrir Operacional" },
  { type: "invalid_date_cte", severity: "warning", title: "CT-e com data de emissão fora do normal",
    why: "A receita cai num mês errado (ou em nenhum) do faturamento e da DRE.",
    action: "Corrija a data de emissão no ERP.", href: "/operacional", hrefLabel: "Abrir Operacional" },
  { type: "negative_open_ar", severity: "warning", title: "Conta a receber com saldo negativo",
    why: "Reduz o saldo a receber e o fluxo de caixa sem que tenha entrado dinheiro.",
    action: "Procure baixa em duplicidade ou desconto lançado a mais.", href: "/financeiro", hrefLabel: "Abrir Financeiro" },
  { type: "negative_open_ap", severity: "warning", title: "Conta a pagar com saldo negativo",
    why: "Reduz o saldo a pagar e o fluxo de caixa sem que tenha saído dinheiro.",
    action: "Procure baixa em duplicidade ou desconto lançado a mais.", href: "/fluxo-caixa", hrefLabel: "Abrir Fluxo de Caixa" },
  { type: "missing_driver_letter", severity: "warning", title: "Carta frete sem motorista",
    why: "O frete pago não entra no custo por motorista.",
    action: "Informe o motorista na carta frete.", href: "/frota/motoristas", hrefLabel: "Abrir Motoristas" },
  { type: "missing_driver_settlement", severity: "warning", title: "Acerto sem motorista",
    why: "O acerto não entra nos números por motorista.",
    action: "Vincule o motorista ao acerto no ERP.", href: "/frota/motoristas", hrefLabel: "Abrir Motoristas" },
  { type: "zero_settlement", severity: "warning", title: "Acerto com fretes e nada a receber pelo motorista",
    why: "O acerto pode estar incompleto e distorcer o custo com motoristas.",
    action: "Revise o acerto no ERP.", href: "/frota/motoristas", hrefLabel: "Abrir Motoristas" },
  { type: "missing_customer_cte", severity: "info", title: "CT-e sem cliente vinculado",
    why: "A receita entra no total, mas some dos rankings e da participação por cliente.",
    action: "Vincule o tomador ao CT-e no ERP.", href: "/operacional", hrefLabel: "Abrir Operacional" },
  { type: "missing_vehicle_letter", severity: "info", title: "Carta frete sem placa",
    why: "O custo não chega ao custo e à margem por placa.",
    action: "Informe a placa na carta frete.", href: "/frota/margem", hrefLabel: "Abrir Margem por Placa" },
];

const BY_TYPE = new Map(CHECKS.map((c) => [c.type, c]));
const SEVERITY_RANK: Record<Severity, number> = { critical: 0, warning: 1, info: 2 };

export const SEVERITY_LABEL: Record<Severity, string> = { critical: "Crítico", warning: "Atenção", info: "Informativo" };

/** Verificação de um código; um código novo no backend ainda aparece, com o próprio nome. */
export function checkOf(type: string, severity: Severity = "warning"): Check {
  return BY_TYPE.get(type) ?? { type, title: type, severity, why: "", action: "", href: "", hrefLabel: "" };
}

export type IssueGroup = { check: Check; issues: DataQualityIssueDto[]; amount: number | null };

const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);

/** Agrupa as ocorrências por verificação: críticas primeiro, depois as mais numerosas. */
export function groupIssues(issues: DataQualityIssueDto[]): IssueGroup[] {
  const groups = new Map<string, IssueGroup>();
  for (const i of issues) {
    const g = groups.get(i.issueType) ?? { check: checkOf(i.issueType, i.severity), issues: [], amount: null };
    g.issues.push(i);
    const v = num(i.evidenceJson?.openAmount);
    if (v != null) g.amount = (g.amount ?? 0) + v;
    groups.set(i.issueType, g);
  }
  // Dentro de cada verificação, o que mais pesa primeiro: maior saldo ou maior salto de km.
  const weight = (i: DataQualityIssueDto) => num(i.evidenceJson?.openAmount) ?? num(i.evidenceJson?.km) ?? 0;
  for (const g of groups.values()) g.issues.sort((a, b) => weight(b) - weight(a));
  return [...groups.values()].sort(
    (a, b) => SEVERITY_RANK[a.check.severity] - SEVERITY_RANK[b.check.severity] || b.issues.length - a.issues.length,
  );
}

/** Registro afetado, como a pessoa procuraria no ERP. */
export function recordLabel(i: DataQualityIssueDto): string {
  const e = i.evidenceJson ?? {};
  if (i.issueType === "implausible_fuel_odometer" && typeof e.plate === "string") return e.plate;
  if (typeof e.customer === "string" && e.customer) return e.customer;
  return i.externalKey?.replace("|", "-") ?? "—";
}

/** Placa para o atalho do veículo (só abastecimento traz placa). */
export function plateOf(i: DataQualityIssueDto): string | null {
  const p = i.evidenceJson?.plate;
  return typeof p === "string" && p ? p : null;
}

const plural = (n: number, one: string, many: string) => `${n.toLocaleString("pt-BR")} ${n === 1 ? one : many}`;

/**
 * Fila "O que pede ação" no formato da Visão Executiva: uma linha por
 * verificação com problema, na ordem dos grupos (críticos e maiores primeiro).
 * Os números abertos são os cinco registros que mais pesam.
 */
export function dataQualityAttention(groups: IssueGroup[]): AttentionPoint[] {
  return groups.map(({ check, issues, amount }, i) => {
    const n = issues.length;
    let fact = `${plural(n, "registro", "registros")} do ERP nesta situação.`;
    let impact = plural(n, "registro", "registros");
    if (check.type === "long_overdue_high_value") {
      fact = `${plural(n, "título a receber vencido", "títulos a receber vencidos")} há mais de 90 dias, cada um acima de R$ 5 mil.`;
      if (amount != null) impact = `${fmtBRL(amount)} em aberto`;
    } else if (check.type === "implausible_fuel_odometer") {
      const plates = new Set(issues.map(plateOf).filter(Boolean)).size;
      fact = `${plural(n, "abastecimento", "abastecimentos")}${plates ? ` de ${plural(plates, "placa", "placas")}` : ""} com salto de odômetro acima de 3.000 km. Ficam fora do km e do km/l da frota.`;
      impact = `${plural(n, "abastecimento", "abastecimentos")} fora do km/l`;
    }
    const evidence = issues.slice(0, 5).map((iss) => {
      const v = iss.evidenceJson ?? {};
      const value = num(v.openAmount) != null ? fmtBRL(num(v.openAmount)!)
        : num(v.km) != null ? `${num(v.km)!.toLocaleString("pt-BR")} km`
        : iss.description;
      return { label: recordLabel(iss), value };
    });
    if (n > 5) evidence.push({ label: `E mais ${(n - 5).toLocaleString("pt-BR")}`, value: "na lista abaixo" });
    return { id: check.type, priority: i + 1, severity: check.severity, title: check.title, fact, impact, action: check.action, evidence, memory: null };
  });
}

/** "19/10/2026 às 02:00" no horário de Brasília. */
export function fmtRunAt(iso: string): string {
  const d = new Date(iso);
  const tz = { timeZone: "America/Sao_Paulo" } as const;
  return `${d.toLocaleDateString("pt-BR", tz)} às ${d.toLocaleTimeString("pt-BR", { ...tz, hour: "2-digit", minute: "2-digit" })}`;
}
